export type PFCAdminContext = {
  restUrl: string
  nonce: string
  postType: string
  postId: number | string
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

export const unwrapResponseJson = (json: unknown): unknown => {
  if (!isRecord(json)) return json
  if ('data' in json) return (json as Record<string, unknown>).data
  return json
}

export const decodeWpRenderedText = (rendered: string): string => {
  if (!rendered) return ''

  // WP core REST commonly returns HTML in `title.rendered` (entities + sometimes tags).
  // Convert it to plain text.
  try {
    if (typeof document !== 'undefined') {
      const el = document.createElement('div')
      el.innerHTML = rendered
      return (el.textContent ?? '').trim()
    }
  } catch {
    // ignore
  }

  // Fallback (non-DOM): strip tags + decode common entities + numeric entities.
  const withoutTags = String(rendered).replace(/<[^>]*>/g, '')
  const decodedNumeric = withoutTags.replace(/&#(\d+);/g, (_m, n) => {
    const codePoint = Number(n)
    if (!Number.isFinite(codePoint) || codePoint <= 0) return ''
    try {
      return String.fromCodePoint(codePoint)
    } catch {
      return ''
    }
  })
  return decodedNumeric
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim()
}

export const coerceEditorDocument = (json: unknown): unknown => {
  const unwrapped = unwrapResponseJson(json)
  if (!isRecord(unwrapped)) return unwrapped

  // Prefer `editor` if present.
  const editor = (unwrapped as Record<string, unknown>).editor
  if (editor) return editor

  // Or prefer `flashcard` if the endpoint returns the emitted payload shape.
  const flashcard = (unwrapped as Record<string, unknown>).flashcard
  if (flashcard) return flashcard

  return unwrapped
}

export const getAdminContext = (): PFCAdminContext | null => {
  const win = window as unknown as { PFC_ADMIN?: unknown }
  const raw = win.PFC_ADMIN
  if (!isRecord(raw)) return null

  const restUrl = typeof raw.restUrl === 'string' ? raw.restUrl : ''
  const nonce = typeof raw.nonce === 'string' ? raw.nonce : ''
  const postType = typeof raw.postType === 'string' ? raw.postType : ''
  const postId = typeof raw.postId === 'number' || typeof raw.postId === 'string' ? raw.postId : ''

  if (!restUrl || !nonce || !postType || postId === '') return null

  return { restUrl, nonce, postType, postId }
}

export const wpFetch = async (url: string, init?: RequestInit, nonce?: string) => {
  const headers = new Headers(init?.headers)
  if (nonce) headers.set('X-WP-Nonce', nonce)

  return fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers,
  })
}

export const wpFetchWithCtx = async (ctx: PFCAdminContext, url: string, init?: RequestInit) =>
  wpFetch(url, init, ctx.nonce)
